/**
 * adminDashboardApi — RTK Query endpoints for everything the dashboard
 * components previously fetched via direct Supabase calls.
 *
 * Compound loads (AttendanceMatrix, ClassOverview) use `queryFn` to run
 * multiple queries + aggregation atomically so the cache key represents
 * the whole derived view. Simpler loads (staff lists, missing-kürzel) use
 * the shared `supabaseBaseQuery`.
 *
 * Mutations (kürzel save) follow the pessimistic `onQueryStarted` pattern
 * from the restructure playbook.
 */
import { rootApi } from "../../../lib/api/rootApi";
import { supabase } from "../../../lib/supabase";
import { getBerlinDateISO } from "../../../utils/dateHelpers";
import type {
  AttendanceFooterStats,
  ClassAttendanceData,
  StaffMissingKurzel,
  StaffSummary,
  StudentDailyLogRow,
} from "../types/adminDashboardTypes";

// ---------------------------------------------------------------------------
// Shapes returned to consumers
// ---------------------------------------------------------------------------

export interface AttendanceMatrixSnapshot {
  classes: ClassAttendanceData[];
  footerStats: AttendanceFooterStats;
}

export interface ClassOverviewRow {
  id: string;
  name: string;
  status:
    | "anwesend"
    | "ausstehend"
    | "entschuldigt"
    | "unentschuldigt"
    | "entlassen";
  lastUpdateTime: string | null;
  lastUpdateBy: string | null;
  note?: string | null;
}

interface ClassOverviewArgs {
  schoolId: string;
  className: string;
}

interface AttendanceMatrixArgs {
  schoolId: string;
}

interface StaffListArgs {
  schoolId: string;
}

interface MissingKurzelArgs {
  schoolId: string;
}

interface UpdateKurzelArgs {
  profileId: string;
  kurzung: string;
  schoolId: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function presenceToStatus(
  presence?: string | null,
): ClassOverviewRow["status"] {
  switch (presence) {
    case "present":
      return "anwesend";
    case "absent_excused":
      return "entschuldigt";
    case "absent_unexcused":
      return "unentschuldigt";
    case "left_early":
    case "left_without_notice":
    case "temporarily_offsite":
      return "entlassen";
    default:
      return "ausstehend";
  }
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export const adminDashboardApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    // --- AttendanceMatrix: single-RPC load for the class grid + footer counts.
    //
    // Pre-refactor this ran 5 serial supabase calls (structure_classes,
    // mv_student_current_class, fn_attendance_present_for_date_scoped,
    // fn_attendance_counts_grouped_clean, student_daily_log) and did all
    // the counting client-side. Now it's one RPC that returns the exact
    // shape the widget needs.
    getAttendanceMatrix: builder.query<
      AttendanceMatrixSnapshot,
      AttendanceMatrixArgs
    >({
      queryFn: async (_args, _queryApi, _extraOptions, baseQuery) => {
        try {
          // Use the browser's wall clock (Berlin) as the date AND the
          // time. This lets Time-Travel-type dev extensions that spoof
          // `Date.now()` drive both the date filter and the überfällig
          // check end-to-end. If we passed null here the server would
          // fall back to its own now()::Europe/Berlin, ignoring any
          // client-side clock spoof.
          const todayISO = getBerlinDateISO();
          const nowBerlinTime = new Date().toLocaleTimeString("de-DE", {
            timeZone: "Europe/Berlin",
            hour12: false,
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          });
          const { data, error } = await baseQuery({
            type: "rpc",
            rpc: "fn_attendance_matrix_today",
            params: { p_date: todayISO, p_now_time: nowBerlinTime },
          });
          if (error) return { error };
          const payload = data as {
            classes?: Array<{
              id: string;
              name: string;
              total_students: number;
              marked_students: number;
              attendance_status: "none" | "partial" | "complete";
            }>;
            footer_stats?: AttendanceFooterStats;
          } | null;

          const classes: ClassAttendanceData[] = (payload?.classes ?? []).map(
            (c) => ({
              id: c.id,
              name: c.name,
              totalStudents: c.total_students,
              markedStudents: c.marked_students,
              attendanceStatus: c.attendance_status,
            }),
          );
          const footerStats: AttendanceFooterStats = payload?.footer_stats ?? {
            anwesend: 0,
            ausstehend: 0,
            entschuldigt: 0,
            unentschuldigt: 0,
            ueberfaellig: 0,
            entlassen: 0,
          };

          return { data: { classes, footerStats } };
        } catch (err) {
          const e = err as { message?: string };
          return {
            error: {
              message: e?.message ?? "Attendance matrix load failed",
              details: "",
              hint: "",
              code: "admin_dashboard_error",
            },
          };
        }
      },
      providesTags: ["Attendance", "ClassStats"],
    }),

    // --- ClassOverview: compound per-class snapshot
    getClassOverview: builder.query<
      { rows: ClassOverviewRow[] },
      ClassOverviewArgs
    >({
      queryFn: async ({ schoolId, className }, _queryApi, _extraOptions, _baseQuery) => {
        try {
          const todayISO = getBerlinDateISO();

          const { data: cls, error: clsErr } = await supabase
            .from("vw_active_classes")
            .select("id")
            .eq("school_id", schoolId)
            .eq("name", className)
            .limit(1)
            .maybeSingle();
          if (clsErr) throw clsErr;
          const classId = cls?.id as string | undefined;
          if (!classId) return { data: { rows: [] } };

          const { data: pi, error: piErr } = await supabase
            .from("vw_student_current_class")
            .select("student_id")
            .eq("school_id", schoolId)
            .eq("class_id", classId);
          if (piErr) throw piErr;
          const studentIds = (pi || [])
            .map((r: { student_id: string }) => r.student_id)
            .filter(Boolean) as string[];
          if (!studentIds.length) return { data: { rows: [] } };

          const { data: names, error: nameErr } = await supabase
            .from("user_profiles")
            .select("id, first_name, last_name")
            .in("id", studentIds);
          if (nameErr) throw nameErr;
          const nameById = new Map<string, string>();
          (names || []).forEach(
            (n: { id: string; first_name: string | null; last_name: string | null }) =>
              nameById.set(
                n.id,
                `${n.first_name || ""} ${n.last_name || ""}`.trim(),
              ),
          );

          const { data: logs, error: logErr } = await supabase
            .from("student_daily_log")
            .select("student_id, presence_status, updated_at, last_updated_by")
            .eq("school_id", schoolId)
            .eq("date", todayISO)
            .in("student_id", studentIds);
          if (logErr) throw logErr;
          const logById = new Map<
            string,
            {
              presence_status?: string;
              updated_at?: string;
              last_updated_by?: string | null;
            }
          >();
          (logs || []).forEach(
            (l: {
              student_id: string;
              presence_status?: string;
              updated_at?: string;
              last_updated_by?: string | null;
            }) => logById.set(l.student_id, l),
          );

          const updaterIds = Array.from(
            new Set(
              (logs || [])
                .map((l: { last_updated_by?: string | null }) => l.last_updated_by)
                .filter(Boolean) as string[],
            ),
          );
          const updaterNameById = new Map<string, string>();
          if (updaterIds.length) {
            const { data: upNames } = await supabase
              .from("user_profiles")
              .select("id, first_name, last_name")
              .in("id", updaterIds);
            (upNames || []).forEach(
              (u: {
                id: string;
                first_name: string | null;
                last_name: string | null;
              }) =>
                updaterNameById.set(
                  u.id,
                  `${u.first_name || ""} ${u.last_name || ""}`.trim(),
                ),
            );
          }

          const { formatTimeBerlin } = await import("../../../utils/dateHelpers");

          const rows: ClassOverviewRow[] = studentIds.map((sid) => {
            const name = nameById.get(sid) || "—";
            const log = logById.get(sid);
            const status = presenceToStatus(log?.presence_status);
            const lastUpdateTime = log?.updated_at
              ? formatTimeBerlin(log.updated_at)
              : null;
            const lastUpdateBy = log?.last_updated_by
              ? updaterNameById.get(log.last_updated_by) || null
              : null;
            return { id: sid, name, status, lastUpdateTime, lastUpdateBy };
          });

          const order: Record<ClassOverviewRow["status"], number> = {
            anwesend: 1,
            ausstehend: 2,
            entschuldigt: 3,
            unentschuldigt: 4,
            entlassen: 5,
          };
          rows.sort(
            (a, b) =>
              order[a.status] - order[b.status] ||
              a.name.localeCompare(b.name, "de"),
          );

          return { data: { rows } };
        } catch (err) {
          const e = err as { message?: string };
          return {
            error: {
              message: e?.message ?? "Class overview load failed",
              details: "",
              hint: "",
              code: "admin_dashboard_error",
            },
          };
        }
      },
      providesTags: ["Attendance"],
    }),

    // --- MissingKurzelPanel: staff without a kürzel
    //
    // Calls a dedicated RPC instead of `vw_react_staff_management`. The view
    // is a kitchen-sink staff projection (studied_subjects JSON agg + phone
    // & email correlated subqueries + 20+ columns) which Postgres can't
    // column-prune through. For a 4-column filtered list, the view took
    // ~370ms. The RPC queries `user_profiles + profile_info_staff + roles`
    // directly and runs in ~5ms.
    //
    // `schoolId` is ignored client-side — the RPC derives it from JWT via
    // public.get_user_school_id(). Kept in the args type for the existing
    // useQuery skip-when-null gating to keep working unchanged.
    getMissingKurzelStaff: builder.query<StaffMissingKurzel[], MissingKurzelArgs>({
      query: () => ({
        type: "rpc",
        rpc: "fn_admin_missing_kurzel",
        params: { p_limit: 10 },
      }),
      transformResponse: (rows: unknown) =>
        ((rows as StaffMissingKurzel[]) || []).map((r) => ({
          id: r.id,
          first_name: r.first_name,
          last_name: r.last_name,
          role_name: r.role_name,
        })),
      providesTags: ["Staff"],
    }),

    updateStaffKurzel: builder.mutation<{ profileId: string }, UpdateKurzelArgs>({
      query: ({ profileId, kurzung }) => ({
        type: "update",
        table: "profile_info_staff",
        params: { data: { kurzung } },
        match: { profile_id: profileId },
      }),
      async onQueryStarted({ profileId, schoolId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Pessimistic patch: remove the saved staffer from the list so the
          // panel updates without a blanket refetch.
          dispatch(
            adminDashboardApi.util.updateQueryData(
              "getMissingKurzelStaff",
              { schoolId },
              (draft) => {
                const idx = draft.findIndex((r) => r.id === profileId);
                if (idx >= 0) draft.splice(idx, 1);
              },
            ),
          );
        } catch {
          // Mutation failed — component state handles its own rollback / toast.
        }
      },
      transformResponse: (_data: unknown, _meta, arg) => ({
        profileId: arg.profileId,
      }),
    }),

    // --- AdminDashboard: staff preload for the personal absence dialog
    getAdminStaffList: builder.query<StaffSummary[], StaffListArgs>({
      query: ({ schoolId }) => ({
        type: "select",
        table: "vw_react_all_staff",
        params: {
          select: "id, first_name, last_name, role_name",
          order: [
            { last_name: { ascending: true } },
            { first_name: { ascending: true } },
          ],
        },
        match: { school_id: schoolId },
      }),
      transformResponse: (rows: unknown) => {
        const list =
          ((rows as Array<{
            id: string;
            first_name?: string | null;
            last_name?: string | null;
            role_name?: string | null;
          }>) || []);
        return list.flatMap((row) => {
          const first = String(row?.first_name || "").trim();
          const last = String(row?.last_name || "").trim();
          const name = `${first} ${last}`.trim();
          if (!row?.id || !name) return [];
          return [
            {
              id: String(row.id),
              name,
              role_name: row?.role_name ? String(row.role_name) : undefined,
            },
          ];
        });
      },
      providesTags: ["Staff"],
    }),
  }),
});

export const {
  useGetAttendanceMatrixQuery,
  useGetClassOverviewQuery,
  useGetMissingKurzelStaffQuery,
  useUpdateStaffKurzelMutation,
  useGetAdminStaffListQuery,
} = adminDashboardApi;
